import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { billingService } from '../services/billingService';
import Navbar from '../components/Navbar';

const SelectPlan = () => {
    const [loading, setLoading] = useState(null); // Track which plan is loading
    const { user } = useAuth();
    const navigate = useNavigate();

    const plans = [
        {
            id: 'free',
            name: 'Free',
            price: '$0',
            priceDetail: '/month',
            features: [
                { name: '10 API calls/min', included: true },
                { name: 'Basic risk analysis', included: true },
                { name: 'Community support', included: true },
                { name: 'Graph visualization', included: false },
                { name: 'Advanced analytics', included: false }
            ],
            buttonText: 'Start Free',
            buttonClass: 'btn-outline-light',
            highlight: false
        },
        {
            id: 'basic',
            name: 'Basic',
            price: '$19.99',
            priceDetail: '/month',
            badge: 'Popular',
            features: [
                { name: '100 API calls/min', included: true },
                { name: 'Full risk analysis', included: true },
                { name: 'Graph visualization', included: true },
                { name: 'Email support', included: true },
                { name: 'API documentation', included: true }
            ],
            buttonText: 'Choose Basic',
            buttonClass: 'btn-light',
            highlight: true,
            cardClass: 'border-primary'
        },
        {
            id: 'enterprise',
            name: 'Enterprise',
            price: '$299',
            priceDetail: '/month',
            features: [
                { name: '1000 API calls/min', included: true },
                { name: 'Advanced analytics', included: true },
                { name: 'Graph visualization', included: true },
                { name: 'Priority support', included: true },
                { name: 'Custom integrations', included: true },
                { name: 'SLA guarantee', included: true }
            ],
            buttonText: 'Choose Enterprise',
            buttonClass: 'btn-warning',
            highlight: false
        }
    ];

    const handleSelectPlan = async (planId) => {
        if (planId === 'free') {
            // No Stripe checkout needed for free plan
            navigate('/app');
            return;
        }

        setLoading(planId);

        try {
            const result = await billingService.createCheckout(planId);

            if (result.ok && result.url) {
                // Redirect to Stripe Checkout
                window.location.href = result.url;
            } else {
                alert('Failed to start checkout. Please try again.');
                setLoading(null);
            }
        } catch (error) {
            console.error('Checkout error:', error);
            alert('Failed to start checkout. Please try again.');
            setLoading(null);
        }
    };

    return (
        <div className="bg-dark text-light" style={{ minHeight: '100vh' }}>
            <Navbar />

            <div className="container my-5">
                <div className="text-center mb-5">
                    <h2>Choose Your Plan</h2>
                    <p className="text-secondary">
                        Select the tier that best fits your needs. You can upgrade or downgrade at any time.
                    </p>
                </div>

                <div className="row g-4 justify-content-center">
                    {plans.map((plan) => (
                        <div key={plan.id} className="col-12 col-md-6 col-lg-4">
                            <div
                                className={`card bg-${plan.highlight ? 'primary' : 'secondary-subtle'} text-light h-100 ${plan.cardClass || ''}`}
                                style={plan.highlight ? { borderWidth: '2px' } : {}}
                            >
                                <div className="card-body d-flex flex-column">
                                    <div className="d-flex justify-content-between align-items-center mb-3">
                                        <h4 className="card-title mb-0">{plan.name}</h4>
                                        {plan.badge && (
                                            <span className="badge bg-warning text-dark">{plan.badge}</span>
                                        )}
                                    </div>

                                    <h2 className="my-3">
                                        {plan.price}
                                        <small className={`${plan.highlight ? 'text-light' : 'text-secondary'} fs-6`}>
                                            {plan.priceDetail}
                                        </small>
                                    </h2>

                                    <ul className="list-unstyled mb-4">
                                        {plan.features.map((feature, index) => (
                                            <li key={index} className="mb-2">
                                                <i
                                                    className={`bi bi-${feature.included ? 'check-circle-fill' : 'x-circle-fill'} ${feature.included ? (plan.highlight ? 'text-light' : 'text-success') : 'text-danger'
                                                        } me-2`}
                                                ></i>
                                                {feature.name}
                                            </li>
                                        ))}
                                    </ul>

                                    <button
                                        className={`btn ${plan.buttonClass} w-100 mt-auto`}
                                        onClick={() => handleSelectPlan(plan.id)}
                                        disabled={loading !== null}
                                    >
                                        {loading === plan.id ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                Processing...
                                            </>
                                        ) : (
                                            plan.buttonText
                                        )}
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                <div className="text-center mt-5">
                    <p className="text-secondary small">
                        All plans include a 14-day money-back guarantee. Cancel anytime.
                    </p>
                    <p className="text-secondary small">
                        Questions? <a href="mailto:support@frwdlabs.xyz" className="text-primary">Contact us</a>
                    </p>
                </div>
            </div>
        </div>
    );
};

export default SelectPlan;

